import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { resendVerification, verifyEmail as verifyEmailApi } from "@/api/auth";
import { ApiError } from "@/api/client";
import { Layout } from "@/components/Layout";
import { NoindexSeo } from "@/components/Seo";
import { useAuthStatus, useInvalidateAuth } from "@/hooks/useAuth";

/**
 * Page atterrissage du lien de vérification d'email.
 * - Si ?token=... : tente la vérification automatiquement.
 * - Sinon : message d'attente "vérifiez votre boîte de réception" + bouton renvoyer.
 */
export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const invalidateAuth = useInvalidateAuth();
  const { user } = useAuthStatus();
  const token = params.get("token");

  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: verifyEmailApi,
    onSuccess: async () => {
      await invalidateAuth();
      setDone(true);
      setTimeout(() => navigate("/dashboard", { replace: true }), 1500);
    },
    onError: (err: ApiError) => {
      if (err.code === "INVALID_TOKEN") {
        setError("Ce lien est invalide ou expiré.");
      } else {
        setError("Une erreur est survenue, veuillez réessayer.");
      }
    },
  });

  useEffect(() => {
    if (token && !mutation.isPending && !mutation.data && !mutation.error) {
      mutation.mutate(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const [resendEmail, setResendEmail] = useState("");
  const [resendMsg, setResendMsg] = useState<string | null>(null);

  const resendMutation = useMutation({
    mutationFn: resendVerification,
    onSuccess: () => {
      setResendMsg("Un nouvel email vous a été envoyé.");
    },
    onError: (err: ApiError) => {
      if (err.status === 429) {
        setResendMsg("Veuillez patienter avant de demander un nouvel email.");
      } else {
        setResendMsg("Une erreur est survenue, veuillez réessayer plus tard.");
      }
    },
  });

  const handleResend = () => {
    const email = resendEmail || user?.email || "";
    if (!email) return;
    setResendMsg(null);
    resendMutation.mutate(email);
  };

  return (
    <Layout>
      <NoindexSeo title="Vérification de l'email" path="/verify-email" />
      <div className="mx-auto max-w-md px-6 py-12 text-center">
        <div className="lg-card p-8">
        <h1 className="text-2xl font-bold">Vérification de l&apos;email</h1>

        {!token && (
          <p className="mt-4 text-gray-700">
            Cliquez sur le lien envoyé par email pour activer votre compte.
            Sans cette étape, vous ne pourrez pas créer d&apos;estimation.
          </p>
        )}

        {token && mutation.isPending && (
          <p className="mt-4 text-gray-700">Vérification en cours…</p>
        )}

        {token && done && (
          <p className="mt-4 text-green-700">Email vérifié. Redirection…</p>
        )}

        {token && error && (
          <p role="alert" className="mt-4 text-red-700">
            {error}
          </p>
        )}

        {!done && (
          <div className="mt-6 border-t pt-6">
            <p className="text-sm text-gray-600 mb-3">
              Vous n&apos;avez pas reçu l&apos;email ?
            </p>
            {!user && (
              <input
                type="email"
                placeholder="Votre adresse email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm mb-3"
              />
            )}
            <button
              type="button"
              onClick={handleResend}
              disabled={resendMutation.isPending}
              className="w-full rounded bg-blue-900 px-4 py-2 text-sm text-white hover:bg-blue-800 disabled:opacity-50"
            >
              {resendMutation.isPending ? "Envoi en cours…" : "Renvoyer l'email"}
            </button>
            {resendMsg && (
              <p role="alert" className="mt-3 text-sm text-gray-700">
                {resendMsg}
              </p>
            )}
          </div>
        )}

        <Link
          to="/login"
          className="mt-8 inline-block text-sm text-blue-900 underline"
        >
          Aller à la page de connexion
        </Link>
        </div>
      </div>
    </Layout>
  );
}